import { directSupabase } from '../../lib/supabaseClient';
import { emitActivityEventBackground } from '../../services/activityEvents.service';
import {
  applyIssuedInvoicesToDrafts,
  issuedDepositLock,
  issuedTotal,
  parseQuotedPattern,
  quoteHasPaymentStructure,
  isOrphanedIssuedStage,
  repriseScheduleStages,
  remainingToInvoice,
  resolveStageStatuses,
  resyncIssuedStageAmounts,
  rollbackIssuedStage,
  scheduleNeedsReview,
  snapshotSchedule,
} from './paymentSchedule';
import type {
  PaymentSchedule,
  PaymentScheduleSource,
  PaymentScheduleStage,
  PlannedStageDraft,
  ScheduleInvoice,
} from './paymentSchedule.types';

function schedulesTable() {
  return (directSupabase as any).from('project_payment_schedules');
}

function stagesTable() {
  return (directSupabase as any).from('project_payment_schedule_stages');
}

function asStage(row: Record<string, unknown>): PaymentScheduleStage {
  return {
    id: String(row.id || ''),
    scheduleId: String(row.schedule_id || ''),
    sortOrder: Number(row.sort_order || 0),
    label: String(row.label || '').trim() || 'Stage',
    kind: (row.kind === 'deposit' || row.kind === 'interim' || row.kind === 'final')
      ? row.kind
      : 'interim',
    plannedPercent: row.planned_percent == null ? null : Number(row.planned_percent),
    plannedAmount: Number(row.planned_amount || 0),
    invoiceId: row.invoice_id ? String(row.invoice_id) : null,
    issuedAmount: row.issued_amount == null ? null : Number(row.issued_amount),
    status: (row.status === 'expected' || row.status === 'ready' || row.status === 'issued')
      ? row.status
      : 'expected',
  };
}

function asSchedule(row: Record<string, unknown>, stages: PaymentScheduleStage[]): PaymentSchedule {
  return {
    id: String(row.id || ''),
    projectId: String(row.project_id || ''),
    source: row.source === 'retrospective' ? 'retrospective' : 'quoted',
    quotedPattern: row.quoted_pattern != null ? String(row.quoted_pattern) : null,
    quotedVersionId: row.quoted_version_id ? String(row.quoted_version_id) : null,
    needsReview: !!row.needs_review,
    agreedValueAtLastCalc: row.agreed_value_at_last_calc == null
      ? null
      : Number(row.agreed_value_at_last_calc),
    createdAt: String(row.created_at || ''),
    createdBy: row.created_by ? String(row.created_by) : null,
    updatedAt: String(row.updated_at || ''),
    stages: [...stages].sort((a, b) => a.sortOrder - b.sortOrder),
  };
}

function stageInsert(scheduleId: string, draft: PlannedStageDraft) {
  return {
    schedule_id: scheduleId,
    sort_order: draft.sortOrder,
    label: String(draft.label || '').trim() || 'Stage',
    kind: draft.kind,
    planned_percent: draft.plannedPercent,
    planned_amount: draft.plannedAmount,
    invoice_id: draft.invoiceId || null,
    issued_amount: draft.issuedAmount ?? null,
    status: draft.status || 'expected',
  };
}

async function actorId(): Promise<string | null> {
  try {
    const { data } = await directSupabase.auth.getUser();
    return data.user?.id || null;
  } catch {
    return null;
  }
}

export async function getProjectPaymentSchedule(projectId: string): Promise<PaymentSchedule | null> {
  const id = String(projectId || '').trim();
  if (!id) return null;
  const { data, error } = await schedulesTable()
    .select('*')
    .eq('project_id', id)
    .maybeSingle();
  if (error) {
    console.warn('Failed to load payment schedule:', error.message);
    return null;
  }
  if (!data) return null;
  const { data: stageRows, error: stageError } = await stagesTable()
    .select('*')
    .eq('schedule_id', data.id)
    .order('sort_order', { ascending: true });
  if (stageError) {
    console.warn('Failed to load payment schedule stages:', stageError.message);
    return asSchedule(data as Record<string, unknown>, []);
  }
  return asSchedule(
    data as Record<string, unknown>,
    ((stageRows || []) as Record<string, unknown>[]).map(asStage),
  );
}

async function replaceStages(scheduleId: string, drafts: PlannedStageDraft[]): Promise<PaymentScheduleStage[]> {
  const { error: deleteError } = await stagesTable().delete().eq('schedule_id', scheduleId);
  if (deleteError) throw new Error(deleteError.message);
  const { data, error } = await stagesTable()
    .insert(drafts.map((draft) => stageInsert(scheduleId, draft)))
    .select('*');
  if (error) throw new Error(error.message);
  return ((data || []) as Record<string, unknown>[]).map(asStage);
}

async function createSchedule(args: {
  projectId: string;
  source: PaymentScheduleSource;
  quotedPattern?: string | null;
  quotedVersionId?: string | null;
  drafts: PlannedStageDraft[];
  grossTotal: number;
  action: 'payment_schedule.created_from_quote' | 'payment_schedule.created_retrospective';
  label: string;
}): Promise<PaymentSchedule> {
  const createdBy = await actorId();
  const { data, error } = await schedulesTable()
    .insert({
      project_id: args.projectId,
      source: args.source,
      quoted_pattern: args.quotedPattern || null,
      quoted_version_id: args.quotedVersionId || null,
      needs_review: false,
      agreed_value_at_last_calc: args.grossTotal,
      created_by: createdBy,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  const stages = await replaceStages(String(data.id), args.drafts);
  const schedule = asSchedule(data as Record<string, unknown>, stages);
  emitActivityEventBackground({
    action: args.action,
    entityType: 'project',
    entityId: schedule.id,
    projectId: args.projectId,
    label: args.label,
    metadata: snapshotSchedule(schedule),
  });
  return schedule;
}

export async function ensureQuotedPaymentSchedule(args: {
  projectId: string;
  quote?: {
    paymentStructureApplicable?: boolean | null;
    paymentStructurePattern?: string | null;
    activeVersionId?: string | null;
    grossTotal?: number | null;
    depositAmount?: number | null;
  } | null;
  invoices: ScheduleInvoice[];
  firstSurveyTransmitted?: boolean;
}): Promise<PaymentSchedule | null> {
  const existing = await getProjectPaymentSchedule(args.projectId);
  if (existing) return existing;
  if (!args.firstSurveyTransmitted) return null;
  if (!quoteHasPaymentStructure(args.quote)) return null;
  const grossTotal = Number(args.quote?.grossTotal || 0);
  const drafts = applyIssuedInvoicesToDrafts({
    drafts: parseQuotedPattern({
      pattern: args.quote?.paymentStructurePattern,
      grossTotal,
      depositAmount: args.quote?.depositAmount,
    }),
    invoices: args.invoices,
    grossTotal,
  });
  if (drafts.length < 2) return null;
  return createSchedule({
    projectId: args.projectId,
    source: 'quoted',
    quotedPattern: args.quote?.paymentStructurePattern || null,
    quotedVersionId: args.quote?.activeVersionId || null,
    drafts,
    grossTotal,
    action: 'payment_schedule.created_from_quote',
    label: 'Payment schedule created from quotation',
  });
}

export async function createRetrospectivePaymentSchedule(args: {
  projectId: string;
  pattern: string;
  grossTotal: number;
  invoices: ScheduleInvoice[];
  depositAmount?: number | null;
  depositPercentage?: number | null;
  drafts?: PlannedStageDraft[];
}): Promise<PaymentSchedule> {
  const existing = await getProjectPaymentSchedule(args.projectId);
  if (existing) throw new Error('This workspace already has a Payment Schedule.');
  // An issued deposit stays exactly as invoiced; later stages split only what is left.
  const lock = issuedDepositLock({
    invoices: args.invoices,
    grossTotal: args.grossTotal,
    depositPercentage: args.depositPercentage,
  });
  const drafts = args.drafts || applyIssuedInvoicesToDrafts({
    drafts: parseQuotedPattern({
      pattern: args.pattern,
      grossTotal: args.grossTotal,
      depositAmount: lock?.amount ?? args.depositAmount,
    }),
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  if (drafts.length < 2) throw new Error('Choose a payment structure with at least two stages.');
  return createSchedule({
    projectId: args.projectId,
    source: 'retrospective',
    quotedPattern: args.pattern,
    quotedVersionId: null,
    drafts,
    grossTotal: args.grossTotal,
    action: 'payment_schedule.created_retrospective',
    label: 'Payment schedule added retrospectively',
  });
}

async function releaseOrphanedStages(args: {
  schedule: PaymentSchedule;
  invoices: ScheduleInvoice[];
  grossTotal: number;
}): Promise<PaymentSchedule> {
  const released = repriseScheduleStages({
    stages: args.schedule.stages,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  if (!released) return args.schedule;
  const orphaned = args.schedule.stages.filter(isOrphanedIssuedStage);
  const stages = await replaceStages(args.schedule.id, released.stages);
  const { data, error } = await schedulesTable()
    .update({ needs_review: released.needsReview })
    .eq('id', args.schedule.id)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  const next = asSchedule(data as Record<string, unknown>, stages);
  if (orphaned.length === 0) return next;
  emitActivityEventBackground({
    action: 'payment_schedule.stage_rolled_back',
    entityType: 'project',
    entityId: next.id,
    projectId: next.projectId,
    label: orphaned.length === 1
      ? `${orphaned[0].label} rolled back`
      : 'Payment schedule milestones rolled back',
    metadata: {
      stageIds: orphaned.map((stage) => stage.id),
      reason: 'invoice_deleted',
      needsReview: released.needsReview,
    },
  });
  return next;
}

async function resyncEditedIssuedStages(args: {
  schedule: PaymentSchedule;
  invoices: ScheduleInvoice[];
  grossTotal: number;
}): Promise<PaymentSchedule> {
  const drafts = resyncIssuedStageAmounts({
    stages: args.schedule.stages,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  if (!drafts) return args.schedule;
  const stages = await replaceStages(args.schedule.id, drafts);
  const next = { ...args.schedule, stages: [...stages].sort((a, b) => a.sortOrder - b.sortOrder) };
  emitActivityEventBackground({
    action: 'payment_schedule.changed',
    entityType: 'project',
    entityId: next.id,
    projectId: next.projectId,
    label: 'Payment schedule updated to an edited invoice amount',
    metadata: {
      previous: snapshotSchedule(args.schedule),
      next: snapshotSchedule(next),
      reason: 'issued_invoice_amount_edited',
    },
  });
  return next;
}

export async function refreshPaymentScheduleState(args: {
  projectId: string;
  grossTotal: number;
  invoices: ScheduleInvoice[];
  finalSettled: boolean;
  firstAccepted?: boolean;
}): Promise<PaymentSchedule | null> {
  const loaded = await getProjectPaymentSchedule(args.projectId);
  if (!loaded) return null;
  const released = await releaseOrphanedStages({
    schedule: loaded,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  const schedule = await resyncEditedIssuedStages({
    schedule: released,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  const resolved = resolveStageStatuses({
    stages: schedule.stages,
    finalSettled: args.finalSettled,
    firstAccepted: args.firstAccepted,
  });
  const needsReview = scheduleNeedsReview({
    grossTotal: args.grossTotal,
    agreedValueAtLastCalc: schedule.agreedValueAtLastCalc,
    stages: resolved,
    invoices: args.invoices,
  });
  const statusChanged = resolved.some((stage, index) => stage.status !== schedule.stages[index]?.status);
  if (statusChanged) {
    await Promise.all(resolved.map((stage) => stagesTable()
      .update({ status: stage.status })
      .eq('id', stage.id)));
  }
  if (needsReview !== schedule.needsReview) {
    await schedulesTable().update({ needs_review: needsReview }).eq('id', schedule.id);
    if (needsReview) {
      emitActivityEventBackground({
        action: 'payment_schedule.review_required',
        entityType: 'project',
        entityId: schedule.id,
        projectId: args.projectId,
        label: 'Payment schedule needs reviewing',
        metadata: {
          previousAgreedValue: schedule.agreedValueAtLastCalc,
          currentAgreedValue: args.grossTotal,
          remainingToInvoice: remainingToInvoice({
            grossTotal: args.grossTotal,
            invoices: args.invoices,
          }),
        },
      });
    }
  }
  return {
    ...schedule,
    stages: resolved,
    needsReview,
  };
}

export async function savePaymentScheduleEdits(args: {
  schedule: PaymentSchedule;
  drafts: PlannedStageDraft[];
  grossTotal: number;
  invoices: ScheduleInvoice[];
}): Promise<PaymentSchedule> {
  const issued = args.schedule.stages.filter((stage) => stage.status === 'issued' || stage.invoiceId);
  const nextIssued = args.drafts.filter((draft) => draft.status === 'issued' || draft.invoiceId);
  if (issued.length !== nextIssued.length) {
    throw new Error('Issued milestones cannot be added or removed.');
  }
  const issuedMoved = issued.some((stage) => {
    const match = nextIssued.find((draft) => draft.invoiceId === stage.invoiceId);
    return !match || Number(match.issuedAmount) !== Number(stage.issuedAmount);
  });
  if (issuedMoved) {
    throw new Error('Issued invoices cannot be changed from the Payment Schedule.');
  }
  const stages = await replaceStages(args.schedule.id, args.drafts);
  const { data, error } = await schedulesTable()
    .update({
      needs_review: false,
      agreed_value_at_last_calc: args.grossTotal,
    })
    .eq('id', args.schedule.id)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  const next = asSchedule(data as Record<string, unknown>, stages);
  emitActivityEventBackground({
    action: 'payment_schedule.changed',
    entityType: 'project',
    entityId: next.id,
    projectId: next.projectId,
    label: 'Payment schedule changed',
    metadata: {
      previous: snapshotSchedule(args.schedule),
      next: snapshotSchedule(next),
      remainingToInvoice: remainingToInvoice({
        grossTotal: args.grossTotal,
        invoices: args.invoices,
      }),
    },
  });
  return next;
}

/**
 * Scrap the schedule to start again. Invoices are untouched: a new schedule maps whatever
 * invoices exist onto its stages. A quote with a payment structure re-creates it on next load.
 */
export async function removePaymentSchedule(args: {
  schedule: PaymentSchedule;
  grossTotal: number;
  invoices: ScheduleInvoice[];
}): Promise<void> {
  const { error } = await schedulesTable().delete().eq('id', args.schedule.id);
  if (error) throw new Error(error.message);
  emitActivityEventBackground({
    action: 'payment_schedule.removed',
    entityType: 'project',
    entityId: args.schedule.id,
    projectId: args.schedule.projectId,
    label: 'Payment schedule removed',
    metadata: {
      previous: snapshotSchedule(args.schedule),
      agreedValue: args.grossTotal,
      invoicedToDate: issuedTotal(args.invoices),
    },
  });
}

export async function bindIssuedScheduleStage(args: {
  schedule: PaymentSchedule;
  stageId: string;
  invoiceId: string;
  issuedAmount: number;
}): Promise<PaymentSchedule> {
  const { error } = await stagesTable()
    .update({
      invoice_id: args.invoiceId,
      issued_amount: args.issuedAmount,
      planned_amount: args.issuedAmount,
      status: 'issued',
    })
    .eq('id', args.stageId)
    .eq('schedule_id', args.schedule.id);
  if (error) throw new Error(error.message);
  const next = await getProjectPaymentSchedule(args.schedule.projectId);
  if (!next) throw new Error('Payment Schedule could not be reloaded.');
  const stage = next.stages.find((item) => item.id === args.stageId);
  emitActivityEventBackground({
    action: 'payment_schedule.stage_issued',
    entityType: 'project',
    entityId: next.id,
    projectId: next.projectId,
    label: stage ? `${stage.label} issued` : 'Payment schedule stage issued',
    metadata: {
      stageId: args.stageId,
      invoiceId: args.invoiceId,
      issuedAmount: args.issuedAmount,
    },
  });
  return next;
}

export async function rollbackPaymentScheduleInvoice(args: {
  projectId: string;
  invoiceId: string;
  invoicesAfterDelete: ScheduleInvoice[];
  grossTotal: number;
}): Promise<PaymentSchedule | null> {
  const schedule = await getProjectPaymentSchedule(args.projectId);
  if (!schedule) return null;
  const bound = schedule.stages.find((stage) => stage.invoiceId === args.invoiceId);
  if (!bound) {
    return releaseOrphanedStages({
      schedule,
      invoices: args.invoicesAfterDelete,
      grossTotal: args.grossTotal,
    });
  }
  const rolled = rollbackIssuedStage({
    stages: schedule.stages,
    invoiceId: args.invoiceId,
    invoicesAfterDelete: args.invoicesAfterDelete,
    grossTotal: args.grossTotal,
  });
  const stages = await replaceStages(schedule.id, rolled.stages);
  const { data, error } = await schedulesTable()
    .update({ needs_review: rolled.needsReview })
    .eq('id', schedule.id)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  const next = asSchedule(data as Record<string, unknown>, stages);
  emitActivityEventBackground({
    action: 'payment_schedule.stage_rolled_back',
    entityType: 'project',
    entityId: next.id,
    projectId: args.projectId,
    label: `${bound.label} rolled back`,
    metadata: {
      stageId: bound.id,
      invoiceId: args.invoiceId,
      issuedAmount: bound.issuedAmount,
      needsReview: rolled.needsReview,
    },
  });
  return next;
}

export const paymentScheduleService = {
  getByProject: getProjectPaymentSchedule,
  ensureFromQuote: ensureQuotedPaymentSchedule,
  createRetrospective: createRetrospectivePaymentSchedule,
  refresh: refreshPaymentScheduleState,
  saveEdits: savePaymentScheduleEdits,
  remove: removePaymentSchedule,
  bindIssuedStage: bindIssuedScheduleStage,
  rollbackInvoice: rollbackPaymentScheduleInvoice,
};
